"use client"

import { useEffect, useLayoutEffect, useMemo, useRef } from "react"

import { usePathname, useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"

import { effectiveScrollY } from "@/lib/hooks/use-scroll-lock"
import {
  browseHistory,
  captureBrowseSnapshot,
  hasBrowseHost,
  installBrowseHistory,
  restoreBrowsePosition,
  restoreBrowseSnapshot,
  returnToBrowseRoot,
  goBackInBrowseHistory,
  subscribeBrowseHistory,
} from "@/platform/app-mode/browse-history"
import { observeImageLookahead } from "@/platform/app-mode/image-lookahead"
import { barButtonSpecs, barTargetPath, regionRoot, pageMetaFor, screenTitleFor } from "@/platform/app-mode/page-meta"
import { screenIsPainted, screenIsProbing, screenIsVisible, subscribeToScreenVisibility } from "@/platform/app-mode/screen-visibility"
import { useAppModeRouter } from "@/platform/app-mode/use-app-mode-router"
import { useBrowseEntry } from "@/platform/app-mode/use-browse-value"
import { useScreenPreparation } from "@/platform/app-mode/use-screen-visible"
import { interceptableUrl, isModifiedClick } from "@/platform/app-mode/visit"
import type { BarButtonData, BrowseCommandData } from "@/platform/bridge/types"
import { useNativeBridge } from "@/platform/bridge/use-native-bridge"
import { useLastCheckoutProductRef } from "@/platform/shopper/carts.client"

const NESTED_CONTROL = "button, input, select, textarea, [role='button']"

const SELF_ROUTED = "[data-app-mode='self-routed']"

const destinationOf = (target: EventTarget | null): string | null => {
  const anchor = target instanceof Element ? target.closest("a[href]") : null
  if (anchor === null) return null
  if (anchor.matches(SELF_ROUTED)) return null
  const control = target instanceof Element ? target.closest(NESTED_CONTROL) : null
  if (control !== null && anchor.contains(control)) return null

  return interceptableUrl(
    {
      href: anchor.getAttribute("href") ?? "",
      target: anchor.getAttribute("target") ?? "",
      download: anchor.getAttribute("download") ?? "",
      rel: anchor.getAttribute("rel") ?? "",
    },
    window.location.href
  )
}

export const AppModeChrome = () => {
  const bridge = useNativeBridge()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const query = searchParams.toString()
  const root = useRef(regionRoot(pathname)).current
  const entryId = useBrowseEntry()
  const router = useAppModeRouter()
  const t = useTranslations("nav")
  const preparing = useScreenPreparation()
  const lastCheckoutProduct = useLastCheckoutProductRef()
  useEffect(() => (bridge.available && preparing ? observeImageLookahead() : undefined), [bridge.available, preparing])
  useLayoutEffect(() => (bridge.available ? installBrowseHistory(root) : undefined), [bridge.available, root])
  useEffect(() => {
    if (bridge.available && pathname === window.location.pathname && query === new URLSearchParams(window.location.search).toString()) restoreBrowsePosition()
  }, [bridge.available, pathname, query, entryId])
  const cartQuantity = lastCheckoutProduct === null ? 0 : 1

  const bar = useMemo<ReadonlyArray<BarButtonData>>(
    () => barButtonSpecs(pathname).map(({ id, icon, labelKey }) => ({ id, label: t(labelKey), icon, ...(id === "cart" ? { badge: cartQuantity } : {}) })),
    [pathname, t, cartQuantity]
  )

  useEffect(() => {
    if (!bridge.available) return

    let posted = ""
    let postedState = ""

    const post = () => {
      if (window.location.pathname !== pathname || new URLSearchParams(window.location.search).toString() !== query) return
      const meta = pageMetaFor({
        url: window.location.href,
        title: screenTitleFor(pathname, document),
        scrollY: effectiveScrollY(),
        bar,
      })
      const serialised = JSON.stringify(meta)
      if (hasBrowseHost()) {
        const state = { url: meta.url, title: meta.title, ...browseHistory(), snapshot: captureBrowseSnapshot() }
        const serialisedState = JSON.stringify(state)
        if (serialisedState !== postedState && bridge.send("browse.state", state)) postedState = serialisedState
      }
      if (serialised === posted) return

      posted = serialised
      bridge.send("page.meta", meta)
    }

    post()

    let frame = 0
    const postOnNextFrame = () => {
      if (frame !== 0) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        post()
      })
    }

    const content = new MutationObserver(postOnNextFrame)
    const unsubscribeHistory = subscribeBrowseHistory(() => {
      postedState = ""
      postOnNextFrame()
    })
    const acceptance = () => `${screenIsVisible()}:${screenIsPainted()}:${screenIsProbing()}`
    let accepting = acceptance()
    const unsubscribeScreen = subscribeToScreenVisibility(() => {
      const now = acceptance()
      if (now === accepting) return
      accepting = now
      if (screenIsProbing() || (!screenIsVisible() && !screenIsPainted())) return

      postedState = ""
      postOnNextFrame()
    })
    let scrollTimer: ReturnType<typeof setTimeout> | undefined
    const postAfterScroll = () => {
      clearTimeout(scrollTimer)
      scrollTimer = setTimeout(post, 120)
    }
    content.observe(document.body, { childList: true, subtree: true })
    window.addEventListener("scroll", postAfterScroll, { passive: true })
    const postOnVisible = () => {
      postedState = ""
      post()
    }
    document.addEventListener("visibilitychange", postOnVisible)

    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame)
      unsubscribeHistory()
      unsubscribeScreen()
      content.disconnect()
      clearTimeout(scrollTimer)
      window.removeEventListener("scroll", postAfterScroll)
      document.removeEventListener("visibilitychange", postOnVisible)
    }
  }, [bar, bridge, pathname, query])

  useEffect(() => {
    if (!bridge.available) return

    const offCommand = bridge.on("browse.command", (payload) => {
      const data = payload as unknown as BrowseCommandData
      const state = browseHistory()
      if (data.entryId !== state.entryId || data.revision !== state.revision) return
      if (data.action === "back" && state.canGoBack) goBackInBrowseHistory((url) => router.navigate(url, { replace: true }))
      if (data.action === "navigate" && typeof data.url === "string") router.navigate(data.url)
      if (data.action === "root") returnToBrowseRoot((url) => router.navigate(url, { replace: true }))
      if (data.action === "restore" && data.snapshot) restoreBrowseSnapshot(data.snapshot)
    })
    const offReselect = bridge.on("tab.reselected", () => returnToBrowseRoot((url) => router.navigate(url, { replace: true })))
    return () => {
      offCommand()
      offReselect()
    }
  }, [bridge, router])

  useEffect(() => {
    if (!bridge.available) return

    return bridge.on("bridge.click", (data) => {
      const target = typeof data.id === "string" ? barTargetPath(pathname, data.id) : null
      if (target === null) return

      router.navigate(target)
    })
  }, [bridge, pathname, router])

  useEffect(() => {
    if (!bridge.available) return

    const onClick = (event: MouseEvent) => {
      if (isModifiedClick(event)) return

      const url = destinationOf(event.target)
      if (url === null) return

      event.preventDefault()
      router.navigate(url)
    }

    const onHint = (event: Event) => {
      const url = destinationOf(event.target)
      if (url !== null) router.hint(url)
    }

    document.addEventListener("click", onClick, true)
    document.addEventListener("pointerover", onHint)
    document.addEventListener("focusin", onHint)

    return () => {
      document.removeEventListener("click", onClick, true)
      document.removeEventListener("pointerover", onHint)
      document.removeEventListener("focusin", onHint)
    }
  }, [bridge, router])

  return null
}
